import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function buildRows({ indicators, values, lang }) {
  return indicators.map((ind) => {
    const v = values[ind.code] || {};
    return {
      code: ind.code,
      name: ind[`name_${lang}`] || ind.name_gl,
      category: ind.category,
      unit: ind.unit,
      value: v.value == null ? "" : v.value,
      provisional: v.provisional ? "yes" : "no",
    };
  });
}

export default function EsgDownloads({ indicators = [], values = {}, lang = "gl" }) {
  const { t } = useTranslation();
  const rows = buildRows({ indicators, values, lang });

  const download = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const csv = () => {
    const header = ["code", "name", "category", "unit", "value", "provisional"];
    const csvText = [
      header.join(","),
      ...rows.map((r) =>
        header.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");
    download(new Blob([csvText], { type: "text/csv" }), "salnes_esg.csv");
    toast.success("CSV");
  };

  const json = () => {
    download(new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" }), "salnes_esg.json");
    toast.success("JSON");
  };

  const xlsx = () => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ESG");
    XLSX.writeFile(wb, "salnes_esg.xlsx");
    toast.success("XLSX");
  };

  const pdf = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(16);
    doc.text(`${t("brand.name")} · Panel ESG`, 14, 18);
    doc.setFontSize(9);
    doc.text("Financiado pola Unión Europea — NextGenerationEU · PRTR", 14, 24);
    doc.setDrawColor(11, 76, 122);
    doc.line(14, 27, 280, 27);
    autoTable(doc, {
      head: [["Código", "Indicador", "Categoría", "Unidade", "Valor", "Provisional"]],
      body: rows.map((r) => [r.code, r.name, r.category, r.unit, r.value, r.provisional]),
      startY: 34,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [11, 76, 122] },
      alternateRowStyles: { fillColor: [251, 250, 247] },
    });
    doc.save("salnes_esg.pdf");
    toast.success("PDF");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="rounded-full"
          data-testid="esg-download-btn"
        >
          <Download size={14} className="mr-2" /> {t("fase2.download")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={csv} data-testid="esg-download-csv">CSV</DropdownMenuItem>
        <DropdownMenuItem onClick={json} data-testid="esg-download-json">JSON</DropdownMenuItem>
        <DropdownMenuItem onClick={xlsx} data-testid="esg-download-xlsx">XLSX</DropdownMenuItem>
        <DropdownMenuItem onClick={pdf} data-testid="esg-download-pdf">PDF institucional</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
